import { buttonVariants } from './Button.styles';
import { Icon } from '@/components/Icons/Icon';
import { TIconName } from '@/design-tokens';
import { cn } from '@/utils/cn';
import React, { useMemo } from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Ref forwarded to the button element.
   */
  forwardRef?: React.Ref<HTMLButtonElement>;

  /**
   * Name of the left icon (from Phosphoricons).
   */
  iconLeft?: TIconName;

  /**
   * Name of the right icon (from Phosphoricons).
   */
  iconRight?: TIconName;

  /**
   * Whether the button is an icon-only button.
   */
  iconOnly?: boolean;

  /**
   * Size of the icon(s) in the button; overrides default sizing.
   */
  iconSizeOverride?: number;

  /**
   * Accessible label for the button (used for screen readers during iconOnly mode).
   */
  label?: string;

  /**
   * Alternative loading state prop for backward compatibility. Use `isLoading` instead.
   */
  loading?: boolean;

  /**
   * Whether the button is in a loading state.
   */
  isLoading?: boolean;

  /**
   * Whether the button is in a transitioning state.
   */
  isTransitioning?: boolean;

  /**
   * Button size. Default is 'medium'.
   */
  size?: 'small' | 'medium';

  /**
   * Button variant which defines the visual style. Default is 'primary'.
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'tertiaryTwoTone' | 'link';
}

export const Button: React.FC<ButtonProps> = ({
  children,
  className,
  disabled,
  forwardRef,
  iconLeft,
  iconRight,
  iconOnly,
  iconSizeOverride,
  isLoading,
  isTransitioning,
  label,
  loading: loadingProp, // for backward compatibility
  onClick,
  size = 'medium',
  type = 'button', // ensure we don't submit forms by default
  variant = 'primary',
  ...props
}) => {
  const loading = isLoading || loadingProp; // Combine both loading props for backward compatibility

  /**
   * An icon-only button is defined as one that either has the `iconOnly` prop
   * set to `true`, or one that has no label but does have an icon provided.
   */
  const isIconOnly = useMemo(() => {
    if (iconOnly !== undefined) {
      return iconOnly;
    }

    if (label || children) {
      return false;
    }

    return Boolean(iconLeft || iconRight);
  }, [iconOnly, label, iconLeft, iconRight, children]);

  /**
   * Determine the icon size based on the button size.
   */
  const iconSize = iconSizeOverride ?? (size === 'small' ? 16 : 24);

  /**
   * Combine all class names into a single string. This includes base styles,
   * variant styles, size styles, and any additional classes passed via props.
   */
  const classNames = cn(buttonVariants({ variant, size, iconOnly: isIconOnly }), className);

  /**
   * Sub-component for rendering an icon. This is used to render both left and right icons
   * with consistent styling and accessibility considerations.
   */
  const IconSubComponent = ({ name }: { name: TIconName }) => {
    const spanClassNames = cn('inline-flex shrink-0', 'pointer-events-none', 'text-inherit', {
      'text-brand-primary': variant === 'tertiaryTwoTone' && !loading,
    });

    return (
      <span className={spanClassNames} aria-hidden="true">
        <Icon name={name} size={iconSize} />
      </span>
    );
  };

  /**
   * Render the left icon element if provided. The icon is wrapped in a span with
   * `aria-hidden="true"` to ensure it does not affect the accessible name of the button.
   */
  const renderIconLeft = iconLeft ? <IconSubComponent name={iconLeft} /> : null;

  /**
   * Render the left icon element if provided. The icon is wrapped in a span with
   * `aria-hidden="true"` to ensure it does not affect the accessible name of the button.
   */
  const renderIconRight = iconRight ? <IconSubComponent name={iconRight} /> : null;

  /**
   * Render a loading spinner icon when the button is in a loading state.
   */
  const renderLoadingIcon =
    loading || isTransitioning ? (
      <span className="pointer-events-none inline-flex shrink-0" aria-hidden="true">
        <svg
          className="h-4 w-4 animate-spin-clockwise animate-duration-1000 animate-iteration-count-infinite"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          style={{ height: iconSize, width: iconSize }}
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </span>
    ) : null;

  return (
    <button
      aria-busy={loading || undefined}
      aria-disabled={disabled || loading || undefined}
      className={classNames}
      data-state={isTransitioning ? 'isTransitioning' : undefined}
      disabled={disabled || loading}
      onClick={onClick}
      ref={forwardRef}
      type={type}
      {...props}
    >
      {loading && renderLoadingIcon}

      {!loading && !isTransitioning && iconLeft && renderIconLeft}

      {label && !children && (
        <>
          {isIconOnly ? (
            <span className="sr-only pointer-events-none">{label}</span>
          ) : (
            <span className="pointer-events-none">{label}</span>
          )}
        </>
      )}

      {children && !label && <span className="pointer-events-none">{children}</span>}

      {iconRight && renderIconRight && (
        <>
          {isIconOnly ? (
            // Render the right icon only if not loading in icon-only mode
            <>{!loading && !isTransitioning && renderIconRight}</>
          ) : (
            // Render the right icon normally when not in icon-only mode
            <>{!isTransitioning && renderIconRight}</>
          )}
        </>
      )}

      {isTransitioning && renderLoadingIcon}
    </button>
  );
};

Button.displayName = 'Button';

export default Button;
